import type { CaptionCue } from './types';

/**
 * Minimal WebVTT parser: enough for Vimeo caption tracks (cue timings,
 * multi-line text, `<v Speaker>` / `<c>` tags). Pure and unit-tested.
 */

const TIMING_RE = /(\d{1,2}:)?(\d{2}):(\d{2})[.,](\d{3})\s*-->\s*(\d{1,2}:)?(\d{2}):(\d{2})[.,](\d{3})/;

function toSeconds(h: string | undefined, m: string, s: string, ms: string): number {
  const hours = h ? parseInt(h.replace(':', ''), 10) : 0;
  return hours * 3600 + parseInt(m, 10) * 60 + parseInt(s, 10) + parseInt(ms, 10) / 1000;
}

function cleanText(raw: string): string {
  return raw
    .replace(/<[^>]+>/g, '') // <v ->, <c.class>, <i>, timestamps tags
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .trim();
}

export function parseVtt(input: string): CaptionCue[] {
  const cues: CaptionCue[] = [];
  const lines = input.replace(/\r\n?/g, '\n').split('\n');
  let i = 0;
  while (i < lines.length) {
    const match = TIMING_RE.exec(lines[i]);
    if (!match) {
      i += 1;
      continue;
    }
    const start = toSeconds(match[1], match[2], match[3], match[4]);
    const end = toSeconds(match[5], match[6], match[7], match[8]);
    i += 1;
    const textLines: string[] = [];
    while (i < lines.length && lines[i].trim() !== '') {
      textLines.push(lines[i]);
      i += 1;
    }
    const text = cleanText(textLines.join('\n'));
    if (text && end > start) cues.push({ start, end, text });
  }
  return cues;
}

/** Binary search for the cue covering `time`, or null between cues. */
export function findCue(cues: CaptionCue[], time: number): CaptionCue | null {
  let lo = 0;
  let hi = cues.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const cue = cues[mid];
    if (time < cue.start) hi = mid - 1;
    else if (time >= cue.end) lo = mid + 1;
    else return cue;
  }
  return null;
}
